import type {
  CameraProfile,
  AreaMissionParameters,
  AreaMissionStats,
  PolygonVertex,
  GridLine,
} from '@/types/route.types'
import { cameraProfiles } from '@/constants/cameraProfiles'
import { EMPTY_AREA_MISSION_STATS } from '@/BackendParser/constants'

/**
 * All photogrammetry formulas in one place.
 * Pure functions — no Vue reactivity, no side effects.
 * Reference: mission-planning/original-combined-plan.md §7
 */

// ─── Helpers ─────────────────────────────────────────────────────────

export const DEG_TO_RAD = Math.PI / 180

/** Minimum positive value for spacing/speed to avoid division by zero or Infinity/NaN */
const EPS = 1e-6
const EARTH_RADIUS = 6371000 // metres

/** Haversine distance between two lat/lng points (metres) */
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = (lat2 - lat1) * DEG_TO_RAD
  const dLon = (lon2 - lon1) * DEG_TO_RAD
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.sin(dLon / 2) ** 2
  return EARTH_RADIUS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** Convert metres to approximate degrees at a given latitude */
export function metresToDegrees(metres: number, latitude: number): { dLat: number; dLon: number } {
  const dLat = (metres / EARTH_RADIUS) * (180 / Math.PI)
  const dLon = (metres / (EARTH_RADIUS * Math.cos(latitude * DEG_TO_RAD))) * (180 / Math.PI)
  return { dLat, dLon }
}

// ─── Core Calculations ───────────────────────────────────────────────

export function getCameraProfile(id: string): CameraProfile | undefined {
  return cameraProfiles.find((c) => c.id === id)
}

/** GSD in cm/px — the primary quality metric */
export function calcGSD(altitude: number, camera: CameraProfile): number {
  return ((altitude * camera.sensorWidth) / (camera.focalLength * camera.imageWidth)) * 100
}

/** Altitude from a target GSD */
export function calcAltitudeFromGSD(targetGSD: number, camera: CameraProfile): number {
  return (targetGSD * camera.focalLength * camera.imageWidth) / (camera.sensorWidth * 100)
}

/**
 * Oblique GSD in cm/px — accounts for camera tilt angle.
 * When the camera is tilted from nadir, the effective GSD increases
 * because the same sensor pixel covers more ground.
 *
 * Formula: obliqueGSD = nadirGSD / cos(tiltFromNadir)
 * where tiltFromNadir = 90° + gimbalPitch (gimbalPitch is negative, e.g. -45°)
 */
export function calcObliqueGSD(altitude: number, camera: CameraProfile, gimbalPitchDeg: number): number {
  const nadirGsd = calcGSD(altitude, camera)
  const tiltFromNadir = Math.abs(90 + gimbalPitchDeg) * DEG_TO_RAD
  const cosAngle = Math.cos(tiltFromNadir)
  // Guard against near-horizontal angles (cos → 0) to avoid infinity
  if (cosAngle < 0.05) return nadirGsd * 20
  return nadirGsd / cosAngle
}

/** GSD in cm/px for the IR (thermal) lens, using the camera's irProfile */
export function calcIRGSD(altitude: number, camera: CameraProfile): number {
  if (!camera.irProfile) return 0
  const ir = camera.irProfile
  return ((altitude * ir.sensorWidth) / (ir.focalLength * ir.imageWidth)) * 100
}

/** Gimbal pitch (degrees, -85 to -40) that yields a target oblique GSD at given altitude. */
export function calcGimbalPitchFromObliqueGSD(
  altitude: number,
  camera: CameraProfile,
  targetObliqueGsd: number,
  clampDeg = { min: -85, max: -40 },
): number {
  const nadirGsd = calcGSD(altitude, camera)
  const target = Math.max(targetObliqueGsd, nadirGsd * 1.0001)
  const ratio = nadirGsd / target
  if (ratio >= 1) return clampDeg.min
  const tiltFromNadirDeg = (Math.acos(ratio) * 180) / Math.PI
  const gimbalPitch = tiltFromNadirDeg - 90
  return Math.max(clampDeg.min, Math.min(clampDeg.max, gimbalPitch))
}

/** Photo footprint on the ground (metres) */
export function calcFootprint(
  altitude: number,
  camera: CameraProfile,
): { width: number; height: number } {
  return {
    width: (camera.sensorWidth / camera.focalLength) * altitude,
    height: (camera.sensorHeight / camera.focalLength) * altitude,
  }
}

/** Distance between parallel flight lines (metres) */
export function calcLineSpacing(footprintWidth: number, sideOverlap: number): number {
  return footprintWidth * (1 - sideOverlap / 100)
}

/** Distance between consecutive photos along a line (metres) */
export function calcPhotoSpacing(footprintHeight: number, forwardOverlap: number): number {
  return footprintHeight * (1 - forwardOverlap / 100)
}

/** Camera trigger interval in seconds */
export function calcTriggerInterval(photoSpacing: number, speed: number): number {
  return photoSpacing / speed
}

/** Polygon area using the Shoelace formula (m²) — works on lat/lng with local projection */
export function calcPolygonArea(vertices: PolygonVertex[]): number {
  if (vertices.length < 3) return 0
  const centroid = getPolygonCentroid(vertices)
  const local = vertices.map((v) =>
    latLonToLocal(v.latitude, v.longitude, centroid.lat, centroid.lon),
  )
  let area = 0
  for (let i = 0; i < local.length; i++) {
    const j = (i + 1) % local.length
    area += local[i].x * local[j].y
    area -= local[j].x * local[i].y
  }
  return Math.abs(area) / 2
}

/** Convert lat/lng to local x/y (metres) relative to an origin */
export function latLonToLocal(
  lat: number,
  lon: number,
  originLat: number,
  originLon: number,
): { x: number; y: number } {
  const x = (lon - originLon) * DEG_TO_RAD * EARTH_RADIUS * Math.cos(originLat * DEG_TO_RAD)
  const y = (lat - originLat) * DEG_TO_RAD * EARTH_RADIUS
  return { x, y }
}

/** Convert local x/y back to lat/lng */
export function localToLatLon(
  x: number,
  y: number,
  originLat: number,
  originLon: number,
): { latitude: number; longitude: number } {
  const latitude = originLat + (y / EARTH_RADIUS) * (180 / Math.PI)
  const longitude =
    originLon + (x / (EARTH_RADIUS * Math.cos(originLat * DEG_TO_RAD))) * (180 / Math.PI)
  return { latitude, longitude }
}

export function getPolygonCentroid(vertices: PolygonVertex[]): { lat: number; lon: number } {
  let lat = 0,
    lon = 0
  for (const v of vertices) {
    lat += v.latitude
    lon += v.longitude
  }
  return { lat: lat / vertices.length, lon: lon / vertices.length }
}

/** Auto-detect the best course angle (along the long axis of the polygon) */
export function calcAutoAngle(vertices: PolygonVertex[]): number {
  if (vertices.length < 2) return 0
  const centroid = getPolygonCentroid(vertices)
  const local = vertices.map((v) =>
    latLonToLocal(v.latitude, v.longitude, centroid.lat, centroid.lon),
  )

  // Use rotating calipers simplified: find the angle that minimises the bounding-box width
  let bestAngle = 0
  let minWidth = Infinity

  for (let deg = 0; deg < 180; deg += 1) {
    const rad = deg * DEG_TO_RAD
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    let minX = Infinity,
      maxX = -Infinity
    for (const p of local) {
      const rx = p.x * cos + p.y * sin
      if (rx < minX) minX = rx
      if (rx > maxX) maxX = rx
    }
    const width = maxX - minX
    if (width < minWidth) {
      minWidth = width
      bestAngle = deg
    }
  }
  return (bestAngle + 90) % 180
}

// ─── Stats Calculator ────────────────────────────────────────────────

/** Compute all stats from polygon + parameters + camera */
export function calcAreaMissionStats(
  vertices: PolygonVertex[],
  params: AreaMissionParameters,
): AreaMissionStats {
  const camera = getCameraProfile(params.cameraProfileId)
  if (!camera || vertices.length < 3) return { ...EMPTY_AREA_MISSION_STATS }

  const gsd = calcGSD(params.altitude, camera)
  const footprint = calcFootprint(params.altitude, camera)
  const lineSpacing = Math.max(calcLineSpacing(footprint.width, params.sideOverlap), EPS)
  const photoSpacing = Math.max(calcPhotoSpacing(footprint.height, params.forwardOverlap), EPS)
  const area = calcPolygonArea(vertices)

  // Estimate number of lines and photos from the polygon bounding dimensions
  const centroid = getPolygonCentroid(vertices)
  const local = vertices.map((v) =>
    latLonToLocal(v.latitude, v.longitude, centroid.lat, centroid.lon),
  )

  const angle = params.courseAngle
  const rad = angle * DEG_TO_RAD
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  // Project onto rotated axes
  let minPerp = Infinity,
    maxPerp = -Infinity
  let minPar = Infinity,
    maxPar = -Infinity
  for (const p of local) {
    const perp = -p.x * sin + p.y * cos // perpendicular to flight direction
    const par = p.x * cos + p.y * sin // parallel to flight direction
    if (perp < minPerp) minPerp = perp
    if (perp > maxPerp) maxPerp = perp
    if (par < minPar) minPar = par
    if (par > maxPar) maxPar = par
  }

  const effectiveWidth = maxPerp - minPerp + params.margin * 2
  const effectiveLength = maxPar - minPar + params.margin * 2

  const numLines = Math.max(1, Math.ceil(effectiveWidth / lineSpacing) + 1)
  const photosPerLine = Math.max(1, Math.ceil(effectiveLength / photoSpacing) + 1)
  const totalPhotos = numLines * photosPerLine * (params.flightPattern === 'oblique' ? 2 : 1)

  // For oblique missions, account for separate oblique speed in time estimate
  const isOblique = params.flightPattern === 'oblique'
  const obliqueSpeed = isOblique && params.obliqueSpeedSeparate
    ? (params.obliqueSpeed || params.speed)
    : params.speed
  const speedClamped = Math.max(params.speed, EPS)
  const obliqueSpeedClamped = Math.max(obliqueSpeed, EPS)
  const orthoFlightDist = numLines * effectiveLength
  const orthoTransitionDist = (numLines - 1) * lineSpacing
  const orthoTotalDist = orthoFlightDist + orthoTransitionDist
  const totalDistance = isOblique ? orthoTotalDist * 2 : orthoTotalDist

  const cruiseTime = isOblique
    ? orthoTotalDist / speedClamped + orthoTotalDist / obliqueSpeedClamped
    : orthoTotalDist / speedClamped
  const turnTime = (numLines - 1) * 3 * (isOblique ? 2 : 1) // ~3 seconds per turn
  const estimatedTime = cruiseTime + turnTime

  // Oblique GSD (derived from tilt angle; 0 when ortho-only)
  // Guard: obliqueGimbalPitch may be undefined on missions created before Phase 2
  const obliqueGsd = isOblique
    ? calcObliqueGSD(params.altitude, camera, params.obliqueGimbalPitch ?? -45)
    : 0

  // Phase 3: IR GSD (dual lens) — only when lens includes IR and camera has irProfile
  const irGsd = (params.lensSelection === 'ir' || params.lensSelection === 'wide,ir')
    ? calcIRGSD(params.altitude, camera)
    : 0

  // Phase 3: Average terrain surface altitude from polygon vertices
  const terrainAltitude = vertices.length > 0
    ? vertices.reduce((sum, v) => sum + (v.height || 0), 0) / vertices.length
    : 0

  return {
    area,
    totalDistance,
    estimatedTime,
    totalPhotos,
    gsd,
    numLines,
    lineSpacing,
    photoSpacing,
    footprintWidth: footprint.width,
    footprintHeight: footprint.height,
    obliqueGsd,
    irGsd,
    terrainAltitude,
  }
}

/**
 * Compute actual total flight distance from the generated grid lines.
 * Sums each flight-line length + each transition (line end → next line start).
 */
export function calcActualDistanceFromGrid(gridLines: GridLine[]): number {
  if (gridLines.length === 0) return 0
  let total = 0
  for (let i = 0; i < gridLines.length; i++) {
    const line = gridLines[i]
    total += haversineDistance(line.start.latitude, line.start.longitude, line.end.latitude, line.end.longitude)
    if (i < gridLines.length - 1) {
      const next = gridLines[i + 1]
      total += haversineDistance(line.end.latitude, line.end.longitude, next.start.latitude, next.start.longitude)
    }
  }
  return total
}

/**
 * Compute expected photo count from the generated grid (clipped to polygon).
 * Uses actual segment lengths, so the count matches what the mission is designed
 * to take (unlike the bounding-box estimate from calcAreaMissionStats).
 *
 * The count differs by photo mode:
 * - **Distance** (`multipleDistance`): action group spans each line segment (start→end).
 *   Trigger fires every N metres along the line only. Photos = sum(lineLength / spacing + 1).
 * - **Timed** (`multipleTiming`): action group is per-waypoint; timer keeps firing every
 *   N seconds from each line-start waypoint, including during the turn/transition to the
 *   next line. Extra photos accumulate while the drone decelerates, turns, and accelerates.
 */
export function calcActualPhotoCountFromGrid(
  gridLines: GridLine[],
  params: AreaMissionParameters,
  orthoLineCount: number,
): number {
  const camera = getCameraProfile(params.cameraProfileId)
  if (!camera || gridLines.length === 0) return 0

  const footprint = calcFootprint(params.altitude, camera)
  const orthoSpacing = Math.max(calcPhotoSpacing(footprint.height, params.forwardOverlap), EPS)
  const obliqueSpacing = Math.max(
    calcPhotoSpacing(footprint.height, params.obliqueForwardOverlap ?? 80),
    EPS,
  )
  const speed = Math.max(params.speed, EPS)

  if (params.photoMode === 'timed') {
    // Timer fires every (photoSpacing / speed) seconds from each line-start waypoint.
    // It keeps running during line traversal AND through the turn to the next line.
    const TURN_TIME = 3 // ~3 seconds per coordinated turn (decel + reaccel)
    let total = 0
    for (let i = 0; i < gridLines.length; i++) {
      const line = gridLines[i]
      const lineLength = haversineDistance(
        line.start.latitude, line.start.longitude,
        line.end.latitude, line.end.longitude,
      )
      const spacing = i < orthoLineCount ? orthoSpacing : obliqueSpacing
      const interval = spacing / speed // trigger interval in seconds
      const lineTime = lineLength / speed
      // Add turn time for all lines except the last (no turn after the final line)
      const turnTime = i < gridLines.length - 1 ? TURN_TIME : 0
      const segmentTime = lineTime + turnTime
      total += Math.max(1, 1 + Math.floor(segmentTime / interval))
    }
    return total
  }

  // Distance mode: photos only along each grid line segment
  let total = 0
  for (let i = 0; i < gridLines.length; i++) {
    const line = gridLines[i]
    const length = haversineDistance(
      line.start.latitude, line.start.longitude,
      line.end.latitude, line.end.longitude,
    )
    const spacing = i < orthoLineCount ? orthoSpacing : obliqueSpacing
    const photosOnLine = Math.max(1, 1 + Math.floor(length / spacing))
    total += photosOnLine
  }
  return total
}
